/**
 * ActivityForm component - Form for creating/editing activities
 * Updated to match AddItinerary.html bottom sheet design
 */

import { useState, useImperativeHandle, forwardRef, FormEvent } from 'react';
import type { Activity, CreateActivityInput } from '../../types/activity';
import type { ParsedPlace } from '../../services/itineraryService';
import PlaceAutocomplete from './PlaceAutocomplete';
import type { PlaceSelectResult } from './PlaceAutocomplete';
import { getPlacePhotoUrl } from '../../utils/photoUrl';
import { ACTIVITY_TYPE_CONFIG } from '../../utils/activityTypes';

interface ActivityFormProps {
  activity?: Activity;
  tripId: string;
  tripStartDate: Date;
  maxDays: number;
  defaultDaySequence?: number;
  isEditMode?: boolean;
  tripName?: string;
  /** Desktop mode: hides the sticky footer submit button (caller renders their own) */
  desktopMode?: boolean;
  onSubmit: (data: CreateActivityInput) => void;
  /** @deprecated 不再被此元件內部使用；保留是為了不影響其他呼叫端既有的 props 型別 */
  onCancel?: () => void;
  /** 只解析連結取得地點資訊，不會建立行程；使用者確認種類後按「新增」才會真的建立 */
  onParseUrl?: (url: string) => Promise<ParsedPlace>;
}

/**
 * Exposed to desktopMode callers so their externally-rendered submit button
 * can trigger the right action. Needed because the manual tab's submit and
 * the paste-link tab's import are different actions with no shared <form>
 * to hook a plain `type="submit" form="..."` button into for the latter.
 */
export interface ActivityFormHandle {
  submit: () => void;
}

type CategoryType = 'activity' | 'stay' | 'travel' | 'dining';
type TabType = 'manual' | 'paste-link';

// Helper function to convert API type to CategoryType
const apiTypeToCategoryType = (apiType?: 'ACTIVITY' | 'STAY' | 'TRANSPORTATION' | 'MEAL'): CategoryType => {
  switch (apiType) {
    case 'ACTIVITY':
      return 'activity';
    case 'STAY':
      return 'stay';
    case 'TRANSPORTATION':
      return 'travel';
    case 'MEAL':
      return 'dining';
    default:
      return 'activity';
  }
};

// Helper function to convert CategoryType to API type
const categoryTypeToApiType = (category: CategoryType): 'ACTIVITY' | 'STAY' | 'TRANSPORTATION' | 'MEAL' => {
  switch (category) {
    case 'activity':
      return 'ACTIVITY';
    case 'stay':
      return 'STAY';
    case 'travel':
      return 'TRANSPORTATION';
    case 'dining':
      return 'MEAL';
  }
};

function ActivityForm(
  {
    activity,
    tripId,
    tripStartDate,
    maxDays,
    defaultDaySequence,
    isEditMode = false,
    tripName,
    desktopMode = false,
    onSubmit,
    onParseUrl,
  }: ActivityFormProps,
  ref: React.Ref<ActivityFormHandle>
) {
  const [tab, setTab] = useState<TabType>('manual');
  const [linkUrl, setLinkUrl] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [parsedPlace, setParsedPlace] = useState<ParsedPlace | null>(null);
  const [category, setCategory] = useState<CategoryType>(apiTypeToCategoryType(activity?.type));
  const [title, setTitle] = useState(activity?.name || '');
  const [locationName, setLocationName] = useState(activity?.location || '');
  const [daySequence, setDaySequence] = useState(defaultDaySequence?.toString() || '');
  const [startTime, setStartTime] = useState(activity?.startTime || '');
  const [endTime, setEndTime] = useState(activity?.endTime || '');
  const [notes, setNotes] = useState(activity?.note || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [placeData, setPlaceData] = useState<PlaceSelectResult | null>(
    activity?.lat != null
      ? {
          name: activity.location,
          lat: activity.lat,
          lng: activity.lng!,
          placeId: activity.placeId!,
          address: activity.address ?? '',
          photoReference: activity.photoReference,
        }
      : null
  );

  const categories = [
    {
      type: 'activity' as CategoryType,
      label: ACTIVITY_TYPE_CONFIG.ACTIVITY.label,
      icon: 'hiking',
      activeColor: 'bg-orange-500 text-white shadow-orange-200',
      inactiveColor: 'bg-slate-100 text-slate-600 hover:bg-orange-50 hover:text-orange-600'
    },
    {
      type: 'stay' as CategoryType,
      label: ACTIVITY_TYPE_CONFIG.STAY.label,
      icon: 'bed',
      activeColor: 'bg-purple-500 text-white shadow-purple-200',
      inactiveColor: 'bg-slate-100 text-slate-600 hover:bg-purple-50 hover:text-purple-600'
    },
    {
      type: 'travel' as CategoryType,
      label: ACTIVITY_TYPE_CONFIG.TRANSPORTATION.label,
      icon: 'flight',
      activeColor: 'bg-blue-500 text-white shadow-blue-200',
      inactiveColor: 'bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-600'
    },
    {
      type: 'dining' as CategoryType,
      label: ACTIVITY_TYPE_CONFIG.MEAL.label,
      icon: 'restaurant',
      activeColor: 'bg-green-500 text-white shadow-green-200',
      inactiveColor: 'bg-slate-100 text-slate-600 hover:bg-green-50 hover:text-green-600'
    },
  ];

  const renderCategoryIcon = (iconName: string) => {
    const iconPaths: Record<string, string> = {
      hiking: ACTIVITY_TYPE_CONFIG.ACTIVITY.icon,
      bed: ACTIVITY_TYPE_CONFIG.STAY.icon,
      flight: ACTIVITY_TYPE_CONFIG.TRANSPORTATION.icon,
      restaurant: ACTIVITY_TYPE_CONFIG.MEAL.icon,
    };
    return iconPaths[iconName] || '';
  };

  const handleParseUrl = async () => {
    if (!linkUrl.trim() || !onParseUrl || isParsing) return;
    setIsParsing(true);
    setParseError('');
    try {
      const result = await onParseUrl(linkUrl.trim());
      setParsedPlace(result);
    } catch (error) {
      setParsedPlace(null);
      setParseError(error instanceof Error ? error.message : '解析失敗，請再試一次');
    } finally {
      setIsParsing(false);
    }
  };

  // 解析出地點、使用者選好類型後才真的呼叫新增行程 API
  const submitParsedPlace = () => {
    if (!parsedPlace) return;

    const submitData: CreateActivityInput = {
      tripId,
      name: parsedPlace.title,
      location: parsedPlace.locationName,
      type: categoryTypeToApiType(category),
      lat: parsedPlace.latitude,
      lng: parsedPlace.longitude,
      placeId: parsedPlace.placeId ?? undefined,
      photoReference: parsedPlace.photoReference ?? undefined,
      address: parsedPlace.address,
    };

    if (daySequence) {
      const dayNum = parseInt(daySequence, 10);
      submitData.dayIndex = dayNum - 1;
      submitData.daySequence = dayNum;
    }

    onSubmit(submitData);
  };

  const submitManual = () => {
    setErrors({});
    const newErrors: Record<string, string> = {};

    // 行程名稱沒填但已選/填地點時，預設用地點名稱當作行程名稱
    const effectiveTitle = title.trim() || locationName.trim();

    if (!effectiveTitle) {
      newErrors.title = '請輸入行程名稱';
    }

    if (!locationName.trim()) {
      newErrors.locationName = '請輸入地點';
    }

    if (daySequence) {
      const dayNum = parseInt(daySequence, 10);
      if (isNaN(dayNum) || dayNum < 1 || dayNum > maxDays) {
        newErrors.daySequence = `請輸入 1 到 ${maxDays} 之間的數字`;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const submitData: CreateActivityInput = {
        tripId,
        name: effectiveTitle,
        location: locationName.trim(),
        note: notes.trim() || undefined,
        type: categoryTypeToApiType(category),
        ...(placeData && {
          lat: placeData.lat,
          lng: placeData.lng,
          placeId: placeData.placeId,
          address: placeData.address,
          photoReference: placeData.photoReference ?? undefined,
        }),
      };

      if (daySequence) {
        const dayNum = parseInt(daySequence, 10);
        submitData.dayIndex = dayNum - 1;
        submitData.daySequence = dayNum;
        submitData.startTime = startTime;
        submitData.endTime = endTime;
        submitData.tripStartDate = tripStartDate;
      }

      onSubmit(submitData);
    } catch (error) {
      if (error instanceof Error) {
        setErrors({ submit: error.message });
      }
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submitManual();
  };

  // Lets desktopMode callers' externally-rendered button do the right thing
  // regardless of which tab is active — see ActivityFormHandle doc comment.
  useImperativeHandle(ref, () => ({
    submit: () => {
      if (tab === 'paste-link') {
        if (parsedPlace) {
          submitParsedPlace();
        } else if (!isParsing) {
          setParseError('請先貼上連結並解析');
        }
      } else {
        submitManual();
      }
    },
  }));

  return (
    <>
      {/* Tab Switcher */}
      <div className="px-6 pt-3 pb-4 border-b border-slate-100">
        <div className="flex rounded-xl bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setTab('manual')}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-all ${
              tab === 'manual'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            手動輸入
          </button>
          <button
            type="button"
            onClick={() => setTab('paste-link')}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-all ${
              tab === 'paste-link'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            貼上連結
          </button>
        </div>
      </div>

      {tab === 'paste-link' ? (
        /* Paste Link Tab */
        <>
          {/* 要能捲動：解析出地點後會多一張預覽卡，內容變長時不捲動會把底部「新增」按鈕擠出彈窗外 */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-5">
            {/* Category Chips — 固定在最上面，貼連結前後都可以先選好種類 */}
            <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
              {categories.map((cat) => (
                <button
                  key={cat.type}
                  type="button"
                  onClick={() => setCategory(cat.type)}
                  className={`flex h-10 shrink-0 items-center justify-center gap-x-2 rounded-lg px-5 transition-all ${
                    category === cat.type
                      ? cat.activeColor
                      : cat.inactiveColor
                  }`}
                >
                  <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                    <path d={renderCategoryIcon(cat.icon)} />
                  </svg>
                  <span className={`text-sm ${category === cat.type ? 'font-semibold' : 'font-medium'}`}>
                    {cat.label}
                  </span>
                </button>
              ))}
            </div>

            <p className="text-sm text-slate-500 leading-relaxed">
              貼上 Google Maps 或 Instagram 的連結，快速將地點加入行程
            </p>

            {/* Link Input + 解析按鈕 */}
            <div className={`flex items-center gap-2 rounded-xl border bg-white pl-4 pr-2 py-2 ${parseError ? 'border-red-400' : 'border-slate-200'}`}>
              <svg className="w-5 h-5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <input
                type="url"
                className="flex-1 text-base text-slate-900 placeholder-slate-400 outline-none bg-transparent py-1.5"
                placeholder="在此貼上連結…"
                value={linkUrl}
                onChange={(e) => {
                  setLinkUrl(e.target.value);
                  if (parseError) setParseError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleParseUrl();
                  }
                }}
                disabled={isParsing}
              />
              <button
                type="button"
                onClick={handleParseUrl}
                disabled={isParsing || !linkUrl.trim()}
                aria-label="解析連結"
                className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 transition-colors ${
                  isParsing || !linkUrl.trim()
                    ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                    : 'bg-[#0dccf2] text-white hover:bg-[#0bbfde]'
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </button>
            </div>
            {parseError && (
              <p className="text-sm text-red-500">{parseError}</p>
            )}

            {/* Supported Platforms */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-slate-400">支援平台</span>
              <div className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1">
                <svg className="w-4 h-4 text-[#34A853]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
                <span className="text-sm text-slate-700 font-medium">Google Maps</span>
              </div>
              <div className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <defs>
                    <linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#f09433" />
                      <stop offset="25%" stopColor="#e6683c" />
                      <stop offset="50%" stopColor="#dc2743" />
                      <stop offset="75%" stopColor="#cc2366" />
                      <stop offset="100%" stopColor="#bc1888" />
                    </linearGradient>
                  </defs>
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="url(#ig-grad)" />
                  <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="1.5" fill="none" />
                  <circle cx="17.5" cy="6.5" r="1.2" fill="white" />
                </svg>
                <span className="text-sm text-slate-700 font-medium">Instagram</span>
              </div>
            </div>

            {/* Loading State */}
            {isParsing && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-[#0dccf2] shrink-0">
                    <svg className="w-3 h-3 text-white animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 3v3m6.364 1.636-2.121 2.121M21 12h-3m-1.636 6.364-2.121-2.121M12 21v-3m-6.364-1.636 2.121-2.121M3 12h3m1.636-6.364 2.121 2.121" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-[#0dccf2]">正在解析連結…</span>
                </div>
                <div className="rounded-2xl bg-[#F7F7F7] overflow-hidden">
                  <div className="h-[90px] bg-[#E8E8E8]" />
                  <div className="p-3 flex flex-col gap-[10px]">
                    <div className="h-4 w-40 rounded-lg bg-[#E0E0E0]" />
                    <div className="h-3 w-[220px] rounded-md bg-[#EBEBEB]" />
                    <div className="h-3 w-[180px] rounded-md bg-[#EBEBEB]" />
                    <div className="h-3 w-[140px] rounded-md bg-[#EBEBEB]" />
                    <div className="h-6 w-[120px] rounded-xl bg-[#E0E0E0]" />
                  </div>
                </div>
              </div>
            )}

            {/* Success State */}
            {parsedPlace && !isParsing && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-[#10B981] shrink-0">
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-[#10B981]">連結解析成功</span>
                </div>
                <div className="rounded-2xl bg-[#F7F7F7] overflow-hidden">
                  {getPlacePhotoUrl(parsedPlace.placeId, 400) ? (
                    <img
                      src={getPlacePhotoUrl(parsedPlace.placeId, 400)!}
                      alt={parsedPlace.title}
                      className="w-full h-[90px] object-cover"
                    />
                  ) : (
                    <div className="h-[90px] bg-[#E8E8E8]" />
                  )}
                  <div className="p-[10px] flex flex-col gap-[6px]">
                    <span className="text-[17px] font-bold text-[#1A1A1A]">{parsedPlace.title}</span>
                    {parsedPlace.locationName && (
                      <div className="flex items-center gap-[6px]">
                        <svg className="w-[14px] h-[14px] shrink-0 text-[#0dccf2]" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                        </svg>
                        <span className="text-[13px] text-[#737373]">{parsedPlace.locationName}</span>
                      </div>
                    )}
                    {parsedPlace.address && (
                      <div className="flex items-center gap-[6px]">
                        <svg className="w-[14px] h-[14px] shrink-0 text-[#A3A3A3]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-[12px] text-[#A3A3A3]">{parsedPlace.address}</span>
                      </div>
                    )}
                    {tripName && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[13px] font-medium text-[#1A1A1A]">加入至：</span>
                        <div className="flex items-center gap-1 bg-[#E0F7FA] rounded-full px-3 py-[6px]">
                          <svg className="w-[14px] h-[14px] text-[#0dccf2]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="text-[12px] font-medium text-[#0dccf2]">{tripName}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer (mobile only) — 一律呼叫新增行程 API，解析完成前停用 */}
          {!desktopMode && (
            <div className="shrink-0 w-full bg-white px-6 py-4 border-t border-[#F0F0F0]">
              <button
                type="button"
                onClick={submitParsedPlace}
                disabled={!parsedPlace || isParsing}
                className={`flex h-[52px] w-full items-center justify-center rounded-[26px] text-base font-semibold text-white transition active:scale-[0.98] ${
                  !parsedPlace || isParsing ? 'bg-[#E0E0E0] cursor-not-allowed' : 'bg-[#0dccf2]'
                }`}
              >
                新增
              </button>
            </div>
          )}
        </>
      ) : (
        /* Manual Tab */
        <>
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            {/* Category Chips */}
            <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
              {categories.map((cat) => (
                <button
                  key={cat.type}
                  type="button"
                  onClick={() => setCategory(cat.type)}
                  className={`flex h-10 shrink-0 items-center justify-center gap-x-2 rounded-lg px-5 transition-all ${
                    category === cat.type
                      ? cat.activeColor
                      : cat.inactiveColor
                  }`}
                >
                  <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                    <path d={renderCategoryIcon(cat.icon)} />
                  </svg>
                  <span className={`text-sm ${category === cat.type ? 'font-semibold' : 'font-medium'}`}>
                    {cat.label}
                  </span>
                </button>
              ))}
            </div>

            {/* Form Fields */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-5" id={desktopMode ? undefined : 'activity-form'}>
              {/* Title Field */}
              <label className="flex flex-col gap-2">
                <span className="text-slate-900 text-sm font-semibold">你要做什麼？</span>
                <input
                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3.5 text-base text-slate-900 placeholder-slate-400 focus:border-[#0dccf2] focus:ring-1 focus:ring-[#0dccf2] outline-none transition-shadow"
                  placeholder="例如：參觀羅浮宮"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoFocus
                />
                {errors.title && <span className="text-red-600 text-sm">{errors.title}</span>}
              </label>

              {/* Date Field - Hidden in edit mode */}
              {!isEditMode && (
                <label className="flex flex-col gap-2">
                  <span className="text-slate-900 text-sm font-semibold">日期</span>
                  <div className="relative">
                    <input
                      className="w-full rounded-lg border border-slate-200 bg-white pl-4 pr-3 py-3.5 text-base text-slate-900 placeholder-slate-400 focus:border-[#0dccf2] focus:ring-1 focus:ring-[#0dccf2] outline-none"
                      type="number"
                      min="1"
                      max={maxDays}
                      placeholder={`第 1-${maxDays} 天`}
                      value={daySequence}
                      onChange={(e) => setDaySequence(e.target.value)}
                    />
                  </div>
                  {errors.daySequence && <span className="text-red-600 text-sm">{errors.daySequence}</span>}
                </label>
              )}

              {/* Time Row - Start and End Time */}
              <div className="flex gap-4">
                <label className="flex flex-col gap-2 flex-1">
                  <span className="text-slate-900 text-sm font-semibold">開始時間</span>
                  <input
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3.5 text-base text-slate-900 placeholder-slate-400 focus:border-[#0dccf2] focus:ring-1 focus:ring-[#0dccf2] outline-none"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-2 flex-1">
                  <span className="text-slate-900 text-sm font-semibold">結束時間</span>
                  <input
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3.5 text-base text-slate-900 placeholder-slate-400 focus:border-[#0dccf2] focus:ring-1 focus:ring-[#0dccf2] outline-none"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </label>
              </div>

              {/* Location Field */}
              <div className="flex flex-col gap-2">
                <span className="text-slate-900 text-sm font-semibold">地點</span>
                <PlaceAutocomplete
                  value={locationName}
                  onInputChange={(v) => {
                    setLocationName(v);
                    if (placeData && v !== placeData.name) {
                      setPlaceData(null);
                    }
                  }}
                  onPlaceSelect={(place) => {
                    setLocationName(place.name);
                    setPlaceData(place);
                  }}
                  placeholder="新增地點"
                  error={errors.locationName}
                />
              </div>

              {/* Notes Field */}
              <label className="flex flex-col gap-2">
                <span className="text-slate-900 text-sm font-semibold">備註</span>
                <textarea
                  className="w-full resize-none rounded-lg border border-slate-200 bg-white px-4 py-3.5 text-base text-slate-900 placeholder-slate-400 focus:border-[#0dccf2] focus:ring-1 focus:ring-[#0dccf2] outline-none min-h-[100px]"
                  placeholder="有什麼要記住的細節嗎？"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                ></textarea>
              </label>

              {errors.submit && <div className="text-red-600 text-sm">{errors.submit}</div>}
            </form>
          </div>

          {/* Sticky Footer Action Bar (mobile only) */}
          {!desktopMode && (
            <div className="sticky bottom-0 w-full bg-white px-6 py-4 border-t border-[#F0F0F0]">
              <button
                type="submit"
                form="activity-form"
                className="flex h-[52px] w-full items-center justify-center rounded-[26px] bg-[#0dccf2] text-base font-semibold text-white transition active:scale-[0.98]"
              >
                {isEditMode ? '更新' : '新增'}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default forwardRef(ActivityForm);
